// @need-translate
import {SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {TalkToast} from '../../../../../../design-system/organisms/TalkToast/TalkToast';
import {TalkToastElement} from '../../../../../../design-system/organisms/TalkToast/ToastElement.interface';
import {theme} from '../../../../../../theme';
import {events} from '../constant/talkViewerEvents';
import {Talk} from '../type/Talk.interface';
import {TalkInfo} from '../type/TalkInfo.interface';
import {TalkMediaStatus} from '../type/TalkMediaStatus.enum';
import {formatTalkViewerMediaTime} from '../util/formatTalkViewerMediaTime';
import {getDataUrlOrUrl} from '../util/getDataUrlOrUrl';
import {setMediaCurrentTime} from '../util/setMediaCurrentTime';

export function TalkViewerBgmContainer() {
  const {
    bgmInfo,
    talkMap,
    talkInfoMap,
    preloadedMedia,
    muted,
    needMediaPlayedDirect,
    isViewerEndExposed,
  } = useTypedSelector(state => ({
    bgmInfo: state.talkViewer.bgm,
    talkMap: state.talkViewer.talkMap,
    talkInfoMap: state.talkViewer.talkInfoMap,
    preloadedMedia: state.talkViewer.preloadedMedia,
    muted: state.talkViewer.internalSettings.muted,
    needMediaPlayedDirect: state.talkViewer.needMediaPlayedDirect,
    isViewerEndExposed: state.viewerEnd.isExposed,
  }));
  const [remainTime, setRemainTime] = useState(0);
  const dispatch = useDispatch();
  const audioRef = useRef<HTMLAudioElement>(null);
  const bgmTalkId = bgmInfo?.talkId;
  const talk = bgmTalkId ? talkMap[bgmTalkId] : ({} as Talk);
  const talkInfo = bgmTalkId ? talkInfoMap[bgmTalkId] : ({} as TalkInfo);
  const media = talk.media;
  const mediaStatus = talkInfo.mediaStatus;
  const showBgm = !!(bgmTalkId && media) && !isViewerEndExposed;
  const remainTimeTimer = useRef(0);

  const toastRef = useRef<TalkToastElement>(null);
  const [toastMsg, setToastMsg] = useState('');

  // muted가 아닐떄의 정상 실행 로직
  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (muted) {
      return;
    }
    if (mediaStatus === TalkMediaStatus.Play) {
      if (needMediaPlayedDirect) {
        return;
      }
      audioRef.current.play();
    } else {
      setMediaCurrentTime(audioRef.current, 0);
      audioRef.current.pause();
    }
  }, [mediaStatus, showBgm, muted, needMediaPlayedDirect]);

  // muted일 때의 실행 로직
  useEffect(() => {
    // 음소거가 아니거나 예외상황에는 기존 setTimeout만 제거하고 return;
    if (!muted || !media || !audioRef.current || !media.source.totalTime) {
      window.clearTimeout(remainTimeTimer.current);
      return;
    }
    // muted 상태에서 bgm은 항상 정지상태
    audioRef.current.pause();
    if (mediaStatus === TalkMediaStatus.Play) {
      let currentRemainTime = remainTime;
      // remainTime이 0이면 아직 시작하지 않은 BGM이므로 totalTime을 remainTime으로 사용
      if (remainTime === 0) {
        currentRemainTime = media.source.totalTime;
      }
      window.clearTimeout(remainTimeTimer.current);
      remainTimeTimer.current = window.setTimeout(() => {
        if (!audioRef.current || !media.source.totalTime) {
          return;
        }
        const nextRemainTime = --currentRemainTime;
        setRemainTime(nextRemainTime);
        // muted가 풀렸을 때 바로 해당 시간에서 재생될 수 있도록 계속 audio의 시간을 변경시켜 줌.
        setMediaCurrentTime(
          audioRef.current,
          media.source.totalTime - nextRemainTime
        );
      }, 1000);
    }
    // 재생상태가 아니면 BGM을 정지처리하고 remainTime과 audio currentTime을 초기화 해 줌
    else {
      window.clearTimeout(remainTimeTimer.current);
      setRemainTime(0);
      setMediaCurrentTime(audioRef.current, 0);
    }
  }, [
    muted,
    remainTime,
    media,
    mediaStatus,
    needMediaPlayedDirect,
    bgmTalkId,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      window.clearTimeout(remainTimeTimer.current);
    };
  }, []);

  // bgm이 변경될 때 마다 remainTime state를 초기화해준다. timeUpdate가 일어나지 않으면 이전 remainTime이 그대로 남아있어서 추가했다.
  useEffect(() => {
    setRemainTime(0);
  }, [bgmTalkId]);

  const onEndedAudio = useCallback(() => {
    if (!bgmTalkId) {
      return;
    }
    dispatch(events.mediaEnded(bgmTalkId));
  }, [bgmTalkId, dispatch]);

  const onTimeUpdateAudio = useCallback(
    (e: SyntheticEvent) => {
      if (muted) {
        return;
      }
      const audioEl = e.target as HTMLAudioElement;
      setRemainTime(audioEl.duration - audioEl.currentTime);
    },
    [muted]
  );

  const onClickBgm = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      if (!bgmTalkId || !audioRef.current) {
        return;
      }
      // BGM은 컴포넌트에서 직접 재생하는 경우 muted 상태면 재생하지 않는다.
      if (needMediaPlayedDirect && muted) {
        setToastMsg(CAN_NOT_PLAY_WITH_MUTED_MSG);
        toastRef.current?.expose();
        return;
      }
      dispatch(events.talkTapPressed(bgmTalkId));
      if (needMediaPlayedDirect) {
        audioRef.current.paused && audioRef.current.play();
      }
    },
    [dispatch, bgmTalkId, needMediaPlayedDirect, muted]
  );

  const onPauseAudio = useCallback(() => {
    // 미디어를 컴포넌트에서 바로 실행해주는 경우 muted상태가 되면 오디오가 정지된다. 이 경우 유저가 알 수 있게 toast를 띄워줌
    if (needMediaPlayedDirect && muted) {
      setToastMsg(FORCED_STOP_BGM_WHEN_MUTED_MSG);
      toastRef.current?.expose();
    }
  }, [muted, needMediaPlayedDirect]);

  if (!showBgm) {
    return null;
  }
  return (
    <>
      <div
        className="absolute bottom-0 left-0 inline-flex cursor-pointer items-center p-30tvs l-safe"
        onClick={onClickBgm}
      >
        {media && (
          <audio
            ref={audioRef}
            controls={false}
            src={getDataUrlOrUrl(media.source.url, preloadedMedia)}
            onTimeUpdate={onTimeUpdateAudio}
            onEnded={onEndedAudio}
            onLoadedData={onTimeUpdateAudio}
            onPause={onPauseAudio}
            /**
             * muted상태에서는 오디오를 재생하지 않고, setTimeout을 통해 오디오의 시간을 조절 해 주는데
             * metadata까지 불러오지 않으면 오디오 정보를 알 수 없어서 currentTime 할당도 불가능하다.
             * 그래서 bgm의 경우에만 metadata를 preload 해 준다.
             */
            preload="metadata"
          />
        )}
        <Icon
          className="mr-4tvs h-26tvs w-26tvs"
          type={
            mediaStatus === TalkMediaStatus.Play
              ? IconType.StopCount
              : IconType.PlayCount
          }
        />
        <Text
          style={{
            textShadow: '0 0 6px rgb(var(--colors-static-mono-black) / 26%)',
            color: 'rgb(var(--colors-static-mono-white) / 90%)',
            fontSize: theme.spacing['24tv'],
          }}
        >
          {formatTalkViewerMediaTime(
            remainTime || media!.source.totalTime || 0
          )}
        </Text>
      </div>
      <TalkToast
        ref={toastRef}
        className="
       pointer-events-none fixed inset-x-0 bottom-120tvs mx-auto flex w-570tvs items-center justify-center
        whitespace-pre-line rounded-4tvs bg-[rgb(var(--colors-static-mono-black)_/_50%)] py-22tvs text-center text-26tvs text-static-mono-white
        "
        exposingTime={3}
        transitionTime={0.5}
      >
        {toastMsg}
      </TalkToast>
    </>
  );
}

const CAN_NOT_PLAY_WITH_MUTED_MSG =
  '음소거 상태입니다.\n음소거를 해제하고 다시 재생해주세요.';
const FORCED_STOP_BGM_WHEN_MUTED_MSG = '배경음이 종료됩니다.';
